import { useEffect, useState } from 'react';
import {
  ApiOutlined,
  AppstoreOutlined,
  CarOutlined,
  ContainerOutlined,
  FileTextOutlined,
  PieChartOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';

// Auto-rotating feature carousel for the left blue branding panel.
// Shows ONE feature card at a time; the card uses a premium glass
// style (border-white/20 + bg-white/8 + soft shadow) tuned for the
// blue brand gradient backdrop.
//
// Behaviour:
//   - Auto-rotates every ROTATE_MS (4500 ms — comfortable read time).
//   - Pauses on hover / focus so users can read a card without it
//     sliding away mid-sentence.
//   - Dots below act as direct nav and reset the rotation timer.
//   - Stable card min-height prevents layout shift between slides.
//   - aria-live on the title makes the announcement screen-reader
//     friendly without being noisy.
//   - Fade-in animation on slide change is purely CSS (keyed wrapper
//     + .onboarding-feature-fade keyframe in index.css).
//
// IMPORTANT: Tailwind preflight is disabled in this project
// (tailwind.config.js -> corePlugins.preflight = false). Browser-
// default <button> styles therefore bleed through unless we reset
// them on every <button> we create — see the dots below where we
// explicitly clear margin / padding / border / background and use
// `appearance-none` so the indicators don't render as ugly grey
// pill-buttons.
//
// No external carousel library is used; just useState + useEffect.

const FEATURES = [
  {
    key: 'gst',
    icon: <ThunderboltOutlined />,
    badge: 'CORE',
    title: 'GST-compliant from day one',
    description:
      'Tax invoices, place-of-supply logic, and append-only ledgers built in.'
  },
  {
    key: 'tenancy',
    icon: <SafetyCertificateOutlined />,
    badge: 'SECURITY',
    title: 'Multi-tenant isolation',
    description:
      'Schema-per-account boundaries keep your data and your customers data apart.'
  },
  {
    key: 'erp',
    icon: <ApiOutlined />,
    badge: 'ERP SCALE',
    title: 'Built for ERP scale',
    description:
      'Inventory, billing, returns, and audits — all on one foundation.'
  },
  {
    key: 'orders',
    icon: <ContainerOutlined />,
    badge: 'CORE',
    title: 'Order Management',
    description:
      'Capture cutting orders with dimensions, glass type, and quantity. Auto-assign to production batches in real time.'
  },
  {
    key: 'production',
    icon: <RocketOutlined />,
    badge: 'OPERATIONS',
    title: 'Production Tracking',
    description:
      'Monitor cutting, tempering, and lamination stages. Get alerts when batches fall behind schedule.'
  },
  {
    key: 'inventory',
    icon: <AppstoreOutlined />,
    badge: 'INVENTORY',
    title: 'Live Inventory',
    description:
      'Track raw glass stock by size, type, and thickness. Automatic low-stock alerts before you run out.'
  },
  {
    key: 'quotations',
    icon: <FileTextOutlined />,
    badge: 'SALES',
    title: 'Auto Quotations',
    description:
      'Generate accurate glass quotations in seconds with built-in rate cards and wastage calculations.'
  },
  {
    key: 'delivery',
    icon: <CarOutlined />,
    badge: 'LOGISTICS',
    title: 'Delivery Scheduling',
    description:
      'Plan dispatch routes, generate challans, and track deliveries to customer sites across your city.'
  },
  {
    key: 'reports',
    icon: <PieChartOutlined />,
    badge: 'INSIGHTS',
    title: 'Reports & Analytics',
    description:
      'Daily production summaries, party-wise ledgers, and GST-ready reports — all exportable to Excel.'
  }
];

const ROTATE_MS = 4500;

// Indicator base + active classes. Centralising avoids fighting
// Tailwind class precedence and makes the browser-default reset
// (border-0, p-0, bg-transparent, appearance-none) explicit on
// every dot.
const DOT_BASE =
  'm-0 p-0 border-0 cursor-pointer appearance-none outline-none ' +
  'transition-all duration-300 rounded-full shrink-0 ' +
  'focus-visible:ring-2 focus-visible:ring-white/60';

export default function FeatureCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  // tickKey forces the timer effect to restart whenever the user
  // taps a dot; without it the auto-rotation would still fire on
  // its original cadence and could advance immediately after a
  // manual selection.
  const [tickKey, setTickKey] = useState(0);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % FEATURES.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, tickKey]);

  function gotoSlide(idx) {
    setActive(idx);
    setTickKey((k) => k + 1);
  }

  const current = FEATURES[active];

  return (
    <section
      aria-label="Glass-i feature highlights"
      className="min-w-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* Glass card. min-h-[180px] keeps the panel from shifting
          while a longer-description slide cycles in. Three-layer
          depth: (1) outer drop shadow lifts the card off the brand
          gradient, (2) inner top hairline highlights the top edge
          (lit-from-above), (3) inner bottom darker line settles the
          base. Reads as polished glass, not a flat translucent box. */}
      <div
        className="relative rounded-2xl border border-white/20 bg-white/[0.08] backdrop-blur-sm p-5 min-h-[180px] min-w-0"
        style={{
          boxShadow:
            '0 12px 32px -12px rgba(8,30,71,0.55), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(15,23,42,0.10)'
        }}
      >
        <div
          key={current.key}
          className="onboarding-feature-fade flex flex-col h-full min-w-0"
        >
          {/* Top row — icon chip on the left, category badge on the
              right. Same grid for every slide so swapping content
              never shifts the layout. */}
          <div className="flex items-center justify-between gap-3 mb-3 min-w-0">
            <span
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-white text-lg shrink-0"
              aria-hidden="true"
            >
              {current.icon}
            </span>
            <span className="inline-flex items-center text-[10px] font-bold tracking-[0.18em] text-white/85 bg-white/10 border border-white/20 rounded-full px-2.5 py-1 whitespace-nowrap shrink-0">
              {current.badge}
            </span>
          </div>

          {/* Title */}
          <h3
            className="text-base sm:text-lg font-bold leading-tight text-white m-0"
            aria-live="polite"
          >
            {current.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-blue-50/90 leading-relaxed m-0 mt-2">
            {current.description}
          </p>
        </div>
      </div>

      {/* Indicators — explicit browser-default reset (m-0 p-0
          border-0 bg-transparent appearance-none) so they don't
          inherit ugly grey pill styling from the user agent. */}
      <div className="mt-4 flex items-center gap-2">
        {FEATURES.map((f, i) => {
          const isActive = i === active;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => gotoSlide(i)}
              aria-label={`Show feature ${i + 1}: ${f.title}`}
              aria-current={isActive ? 'true' : undefined}
              className={`${DOT_BASE} h-1.5 ${
                isActive
                  ? 'w-6 bg-white'
                  : 'w-1.5 bg-white/30 hover:bg-white/55'
              }`}
            />
          );
        })}
      </div>
    </section>
  );
}
